<template>
    <div class="shopping-view pb-5">
        <!-- Plattform-Hinweis: Shopping wird aktuell nur für ChatGPT getrackt -->
        <v-card v-if="platformNotSupported" class="rounded_card quaroshadow platform-empty-state pa-10 text-center">
            <div v-if="platformLogo" class="platform-empty-state__logo mb-3" v-html="platformLogo"></div>
            <v-icon v-else size="42" color="grey lighten-1" class="mb-3">mdi-cart-off</v-icon>
            <h3 class="platform-empty-state__title">No shopping results for {{ platformLabel }}</h3>
            <p class="platform-empty-state__text">
                {{ platformLabel }} doesn't return structured shopping results yet — shopping carousels are currently only detected in <strong>ChatGPT</strong> responses. As soon as other
                platforms roll out shopping features, they will appear here automatically.
            </p>
            <v-btn rounded color="primary" class="mt-2" @click="$emit('switchPlatform', 'chatgpt')">
                <v-icon small left>mdi-swap-horizontal</v-icon>
                Show ChatGPT shopping data
            </v-btn>
        </v-card>

        <template v-else>
            <!-- KPI Cards -->
            <v-row class="mt-1">
                <v-col v-for="card in kpiCards" :key="card.key" cols="12" sm="6" md="3">
                    <v-card class="rounded_card quaroshadow pa-4 kpi-card">
                        <div class="d-flex align-center">
                            <v-icon color="primary" class="mr-3">{{ card.icon }}</v-icon>
                            <div class="flex-grow-1">
                                <div class="kpi-value">
                                    {{ loadingOverview ? "–" : card.value }}<span v-if="!loadingOverview && card.total != null" class="kpi-total">/ {{ card.total }}</span>
                                </div>
                                <div class="kpi-label d-flex align-center">
                                    {{ card.label }}
                                    <v-tooltip top max-width="330">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                        </template>
                                        <span>{{ card.tooltip }}</span>
                                    </v-tooltip>
                                </div>
                                <div v-if="rangeSubtitle" class="kpi-range">{{ rangeSubtitle }}</div>
                            </div>
                            <v-chip
                                v-if="!loadingOverview && card.delta != null"
                                x-small
                                :color="card.delta >= 0 ? 'green lighten-5' : 'red lighten-5'"
                                :text-color="card.delta >= 0 ? 'green darken-2' : 'red darken-2'"
                                class="kpi-delta">
                                <v-icon x-small left>{{ card.delta >= 0 ? "mdi-arrow-up" : "mdi-arrow-down" }}</v-icon>
                                {{ Math.abs(card.delta).toFixed(1) }}%
                            </v-chip>
                            <v-chip v-else-if="!loadingOverview && card.isNew" x-small color="blue lighten-5" text-color="blue darken-2" class="kpi-delta">new</v-chip>
                        </div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card class="rounded_card quaroshadow pa-4 kpi-card">
                        <div class="d-flex align-center">
                            <v-icon color="amber darken-2" class="mr-3">mdi-trophy-outline</v-icon>
                            <div v-if="buyboxLeader">
                                <div class="kpi-value kpi-value--small d-flex align-center">
                                    <img v-if="buyboxLeader.merchant_domain" :src="faviconUrl(buyboxLeader.merchant_domain)" class="merchant-favicon mr-2" alt="" />
                                    <span class="kpi-leader-name">{{ buyboxLeader.merchant_label || buyboxLeader.merchant }}</span>
                                </div>
                                <div class="kpi-label d-flex align-center">
                                    Buybox Leader ({{ buyboxLeader.buyboxWins }} wins)
                                    <v-tooltip top max-width="330">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>The provider that most often held position 1 of a carousel in this range — the seller users see first. Ties are broken by total appearances.</span>
                        </v-tooltip>
                                </div>
                            </div>
                            <div v-else>
                                <div class="kpi-value">–</div>
                                <div class="kpi-label">Buybox Leader</div>
                            </div>
                        </div>
                        <div v-if="rangeSubtitle" class="kpi-range kpi-range--indent">{{ rangeSubtitle }}</div>
                    </v-card>
                </v-col>
            </v-row>

            <div v-if="!loadingOverview && overview.totalRuns > 0 && overview.runsWithShopping === 0" class="shopping-notice mt-3">
                <v-icon small color="primary" class="mr-2">mdi-information-outline</v-icon>
                <span>
                    No shopping results were detected in the selected date range.
                    <span class="shopping-notice__hint">{{ formatNumber(overview.totalRuns) }} runs were checked — try a wider date range.</span>
                </span>
            </div>

            <!-- aktiver Merchant-Filter (Klick auf Chart-Balken) -->
            <v-row v-if="merchantFilter" class="mt-1" no-gutters>
                <v-chip close color="primary" text-color="white" small class="mt-2" @click:close="clearMerchantFilter">
                    <v-icon x-small left>mdi-filter</v-icon>
                    {{ merchantFilter }}
                </v-chip>
                <span class="filter-hint mt-3 ml-2">Filtering all tables by this provider — click × to clear.</span>
            </v-row>

            <!-- Charts -->
            <v-row class="mt-1">
                <v-col cols="12" md="6">
                    <v-card class="rounded_card quaroshadow pa-4">
                        <div class="chart-title d-flex align-center">
                            Top Providers by Appearances
                            <v-tooltip top max-width="340">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>Sellers ranked by how many carousel slots they filled in this range. Clicking a bar filters every table on this page to that provider.</span>
                        </v-tooltip>
                        </div>
                        <div class="chart-subtitle">Providers ranked by total appearances — click a bar to filter</div>
                        <div class="chart-wrap">
                            <bar-chart v-if="providersChartData.labels.length" :key="'prov-' + chartKey + '-' + merchantFilter" :dataChart="providersChartData" :options="providersChartOptions" />
                            <div v-else class="chart-empty">No provider data</div>
                        </div>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card class="rounded_card quaroshadow pa-4">
                        <div class="chart-title d-flex align-center">
                            Runs with/without Shopping
                            <v-tooltip top max-width="340">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>Per import day: how many prompt runs returned a shopping carousel (green) versus none (grey). Shows how the base rate of shopping develops over time.</span>
                        </v-tooltip>
                        </div>
                        <div class="chart-subtitle">Distribution of runs with shopping results over time</div>
                        <div class="chart-wrap">
                            <bar-chart v-if="runsChartData.labels.length" :key="'runs-' + chartKey" :dataChart="runsChartData" :options="stackedChartOptions" />
                            <div v-else class="chart-empty">No timeline data</div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-card class="rounded_card quaroshadow pa-4">
                        <div class="chart-title d-flex align-center">
                            Buybox Winner Analysis
                            <v-tooltip top max-width="340">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>How often each provider reached position 1 of a carousel — the slot shown first. Click a bar to filter the page by that provider.</span>
                        </v-tooltip>
                        </div>
                        <div class="chart-subtitle">Providers most frequently ranked #1 — click a bar to filter</div>
                        <div class="chart-wrap">
                            <bar-chart v-if="buyboxChartData.labels.length" :key="'buybox-' + chartKey + '-' + merchantFilter" :dataChart="buyboxChartData" :options="buyboxChartOptions" />
                            <div v-else class="chart-empty">No buybox data</div>
                        </div>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card class="rounded_card quaroshadow pa-4">
                        <div class="chart-title d-flex align-center">
                            Buybox Winners Timeline
                            <v-tooltip top max-width="340">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>Share of position-1 slots per provider and day, stacked to 100%. The top 8 providers are shown individually, the rest grouped as „Other“.</span>
                        </v-tooltip>
                        </div>
                        <div class="chart-subtitle">Distribution of providers ranking #1 over time, stacked to 100%</div>
                        <div class="chart-wrap">
                            <bar-chart v-if="buyboxTimelineData.labels.length" :key="'bbtl-' + chartKey" :dataChart="buyboxTimelineData" :options="buyboxTimelineOptions" />
                            <div v-else class="chart-empty">No timeline data</div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Providers Tabelle (volle Breite) -->
            <v-card class="rounded_card quaroshadow pa-4 mt-4">
                <div class="d-flex align-center flex-wrap mb-2">
                    <div>
                        <div class="chart-title d-flex align-center">
                            Providers
                            <v-tooltip top max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                </template>
                                <span>Every seller that appeared in a shopping carousel in this range, with its share of visible slots, position-1 wins and average rank. Click a row for its products and prompts.</span>
                            </v-tooltip>
                        </div>
                        <div class="chart-subtitle mb-0">All providers with visibility metrics{{ brandDomain ? " — your brand is highlighted" : "" }}</div>
                    </div>
                    <v-spacer />
                    <v-text-field v-model="providerSearch" class="table-search mr-2" rounded dense filled clearable clear-icon="mdi-close" prepend-inner-icon="mdi-magnify" label="Search providers..." single-line hide-details />
                    <v-btn icon color="primary" :disabled="!filteredProviders.length" @click="exportProviders" aria-label="Export providers">
                        <v-icon>mdi-cloud-download</v-icon>
                    </v-btn>
                </div>
                <v-data-table
                    :headers="providerHeaders"
                    :items="filteredProviders"
                    :loading="loadingProviders"
                    :items-per-page="10"
                    :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
                    class="transparent-table provider-table"
                    :item-class="providerRowClass"
                    @click:row="openProviderPanel">
                    <!-- Spalten-Tooltips aus der Header-Definition -->
                    <template v-for="h in providerHeaders.filter((x) => x.tooltip)" v-slot:[`header.${h.value}`]="{ header }">
                        <span :key="h.value" class="sv-head-wrap">
                            <span>{{ header.text }}</span>
                            <v-tooltip top max-width="330">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                </template>
                                <span>{{ h.tooltip }}</span>
                            </v-tooltip>
                        </span>
                    </template>
                    <template v-slot:[`item.merchant`]="{ item }">
                        <span class="d-flex align-center">
                            <img v-if="item.merchant_domain" :src="faviconUrl(item.merchant_domain)" class="merchant-favicon mr-2" alt="" />
                            <v-icon v-else small class="mr-2">mdi-storefront-outline</v-icon>
                            {{ item.merchant_label || item.merchant }}
                            <v-icon v-if="isBrandMerchant(item)" small color="primary" class="ml-1">mdi-star</v-icon>
                        </span>
                    </template>
                    <template v-slot:[`item.share`]="{ item }">
                        <div class="d-flex align-center">
                            <span class="share-value mr-2">{{ item.share.toFixed(1) }}%</span>
                            <v-progress-linear :value="item.share" color="primary" background-color="grey lighten-3" rounded height="5" style="max-width: 70px" />
                        </div>
                    </template>
                    <template v-slot:[`item.buyboxWins`]="{ item }">
                        <v-chip v-if="item.buyboxWins > 0" x-small color="amber lighten-4" text-color="amber darken-4">{{ item.buyboxWins }}</v-chip>
                        <span v-else>–</span>
                    </template>
                    <template v-slot:[`item.rankAvg`]="{ item }">{{ item.rankAvg ? item.rankAvg.toFixed(1) : "–" }}</template>
                </v-data-table>
            </v-card>

            <!-- What ChatGPT Searches For: Keyword-Auswertung über alle Shopping-Queries -->
            <v-card class="rounded_card quaroshadow pa-4 mt-4">
                <div class="d-flex align-center flex-wrap mb-2">
                    <div>
                        <div class="chart-title d-flex align-center">
                            What ChatGPT Searches For
                            <v-tooltip top max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                </template>
                                <span>Individual words across all distinct shopping queries. The number counts how many <em>queries</em> contain the term, not how often it was sent. Filler words are removed; brand and commercial words are kept, because it matters when the model adds them itself.</span>
                            </v-tooltip>
                        </div>
                        <div class="chart-subtitle mb-0">Most frequent terms across all shopping queries — click a term to filter the table below</div>
                    </div>
                    <v-spacer />
                    <v-btn icon color="primary" :disabled="!queryKeywords.length" @click="exportKeywords" aria-label="Export keywords">
                        <v-icon>mdi-cloud-download</v-icon>
                    </v-btn>
                </div>
                <div v-if="loadingQueries && !queryKeywords.length" class="keyword-empty">Loading…</div>
                <div v-else-if="!queryKeywords.length" class="keyword-empty">No shopping queries in this range</div>
                <div v-else>
                    <v-chip
                        v-for="kw in visibleKeywords"
                        :key="kw.term"
                        :class="['kw-chip', keywordTier(kw)]"
                        :color="activeKeyword === kw.term ? 'primary' : 'teal lighten-5'"
                        :text-color="activeKeyword === kw.term ? 'white' : 'teal darken-2'"
                        small
                        @click="toggleKeyword(kw.term)">
                        {{ kw.term }}
                        <span class="kw-chip__count">{{ kw.queryCount }}</span>
                        <v-icon v-if="activeKeyword === kw.term" x-small right>mdi-close</v-icon>
                    </v-chip>
                    <v-btn v-if="queryKeywords.length > 30" text x-small color="primary" class="ml-1" @click="keywordsExpanded = !keywordsExpanded">
                        {{ keywordsExpanded ? "Show less" : `Show all ${queryKeywords.length}` }}
                    </v-btn>
                </div>
            </v-card>

            <!-- Shopping Queries: die internen Katalog-Suchanfragen -->
            <v-card class="rounded_card quaroshadow pa-4 mt-4">
                <div class="d-flex align-center flex-wrap mb-2">
                    <div>
                        <div class="chart-title d-flex align-center">
                            Shopping Queries
                            <v-tooltip top max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                </template>
                                <span>The searches ChatGPT ran against the shopping catalog to assemble the carousels — shown verbatim. These are the exact terms your product titles and feed attributes need to match.</span>
                            </v-tooltip>
                        </div>
                        <div class="chart-subtitle mb-0">{{ queriesSubtitle }}</div>
                    </div>
                    <v-spacer />
                    <v-switch v-model="recurringOnly" dense hide-details class="mt-0 mr-4 zero-prompts-switch" label="Recurring only" />
                    <v-text-field v-model="querySearch" class="table-search mr-2" rounded dense filled clearable clear-icon="mdi-close" prepend-inner-icon="mdi-magnify" label="Search queries..." single-line hide-details />
                    <v-btn icon color="primary" :disabled="!queriesTotal" :loading="exportingQueries" @click="exportQueries" aria-label="Export queries">
                        <v-icon>mdi-cloud-download</v-icon>
                    </v-btn>
                </div>
                <v-data-table
                    :headers="queryHeaders"
                    :items="filteredQueries"
                    :loading="loadingQueries"
                    :server-items-length="queriesTotal"
                    :options.sync="queryOptions"
                    :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
                    class="transparent-table query-table"
                    @click:row="openQueryPanel">
                    <!-- Spalten-Tooltips aus der Header-Definition -->
                    <template v-for="h in queryHeaders.filter((x) => x.tooltip)" v-slot:[`header.${h.value}`]="{ header }">
                        <span :key="h.value" class="sv-head-wrap">
                            <span>{{ header.text }}</span>
                            <v-tooltip top max-width="330">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                </template>
                                <span>{{ h.tooltip }}</span>
                            </v-tooltip>
                        </span>
                    </template>
                    <template v-slot:[`item.query`]="{ item }">
                        <span class="d-flex align-center">
                            <v-icon x-small color="grey" class="mr-2">mdi-magnify</v-icon>
                            <span class="query-cell" :title="item.query">{{ item.query }}</span>
                        </span>
                    </template>
                    <template v-slot:[`item.merchants`]="{ item }">
                        <v-chip v-for="m in (item.merchants || []).slice(0, 3)" :key="m" x-small class="mr-1 merchant-chip" outlined @click.stop="setMerchantFilter(m)">{{ m }}</v-chip>
                        <span v-if="!(item.merchants || []).length">–</span>
                    </template>
                    <template v-slot:[`item.buyboxCount`]="{ item }">
                        <v-chip v-if="item.buyboxCount > 0" x-small color="amber lighten-4" text-color="amber darken-4">{{ item.buyboxCount }}</v-chip>
                        <span v-else>–</span>
                    </template>
                    <template v-slot:[`item.priceAvg`]="{ item }">
                        <div v-if="item.priceAvg != null">
                            <strong>{{ formatPrice(item.priceAvg, item.currency) }}</strong>
                            <div v-if="item.priceMin != null && item.priceMax != null && item.priceMin !== item.priceMax" class="price-range">
                                {{ formatPrice(item.priceMin, item.currency) }} – {{ formatPrice(item.priceMax, item.currency) }}
                            </div>
                        </div>
                        <span v-else>–</span>
                    </template>
                    <template v-slot:[`item.dayCount`]="{ item }">
                        <v-chip x-small :color="item.dayCount > 1 ? 'primary' : 'grey lighten-3'" :text-color="item.dayCount > 1 ? 'white' : 'grey darken-1'">{{ item.dayCount }}d</v-chip>
                    </template>
                    <template v-slot:[`item.lastSeen`]="{ item }">
                        <div class="date-cell">Last: {{ formatDate(item.lastSeen) }}</div>
                        <div class="date-cell grey--text">First: {{ formatDate(item.firstSeen) }}</div>
                    </template>
                </v-data-table>
            </v-card>

            <!-- Prompts with Shopping -->
            <v-card class="rounded_card quaroshadow pa-4 mt-4">
                <div class="d-flex align-center flex-wrap mb-2">
                    <div>
                        <div class="chart-title d-flex align-center">
                            Prompts with Shopping
                            <v-tooltip top max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                </template>
                                <span>Which of your tracked prompts triggered a shopping carousel, and how reliably. Answers the tracking question; the queries above answer the optimisation question.</span>
                            </v-tooltip>
                        </div>
                        <div class="chart-subtitle mb-0">
                            {{ promptsWithShoppingCount }} of {{ prompts.length }} tracked prompts triggered a shopping carousel — the queries above show what was actually searched
                        </div>
                    </div>
                    <v-spacer />
                    <v-switch v-model="showZeroPrompts" dense hide-details class="mt-0 mr-4 zero-prompts-switch" label="Include prompts without shopping" />
                    <v-text-field v-model="promptSearch" class="table-search mr-2" rounded dense filled clearable clear-icon="mdi-close" prepend-inner-icon="mdi-magnify" label="Search prompts..." single-line hide-details />
                    <v-btn icon color="primary" :disabled="!filteredPrompts.length" @click="exportPrompts" aria-label="Export prompts">
                        <v-icon>mdi-cloud-download</v-icon>
                    </v-btn>
                </div>
                <v-data-table
                    :headers="promptHeaders"
                    :items="filteredPrompts"
                    :loading="loadingPrompts"
                    :items-per-page="10"
                    class="transparent-table prompt-table"
                    @click:row="(item) => $emit('openPrompt', item)">
                    <!-- Spalten-Tooltips aus der Header-Definition -->
                    <template v-for="h in promptHeaders.filter((x) => x.tooltip)" v-slot:[`header.${h.value}`]="{ header }">
                        <span :key="h.value" class="sv-head-wrap">
                            <span>{{ header.text }}</span>
                            <v-tooltip top max-width="330">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                </template>
                                <span>{{ h.tooltip }}</span>
                            </v-tooltip>
                        </span>
                    </template>
                    <template v-slot:[`item.prompt`]="{ item }">
                        <span class="prompt-cell">{{ item.prompt }}</span>
                    </template>
                    <template v-slot:[`item.shopping`]="{ item }">
                        <v-chip x-small :color="item.runsWithShopping > 0 ? 'primary' : 'grey lighten-2'" :text-color="item.runsWithShopping > 0 ? 'white' : 'grey darken-1'">
                            {{ item.runsWithShopping }} of {{ item.totalRuns }}
                        </v-chip>
                    </template>
                    <template v-slot:[`item.topMerchants`]="{ item }">
                        <v-chip v-for="m in item.topMerchants.slice(0, 3)" :key="m.merchant" x-small class="mr-1 merchant-chip" outlined @click.stop="setMerchantFilter(m.merchant)">
                            {{ m.merchant }}
                        </v-chip>
                        <span v-if="!item.topMerchants.length">–</span>
                    </template>
                    <template v-slot:[`item.buyboxWinners`]="{ item }">
                        <v-chip
                            v-for="m in item.buyboxWinners.slice(0, 2)"
                            :key="m.merchant"
                            x-small
                            class="mr-1 merchant-chip"
                            color="amber lighten-4"
                            text-color="amber darken-4"
                            @click.stop="setMerchantFilter(m.merchant)">
                            {{ m.merchant }}
                        </v-chip>
                        <span v-if="!item.buyboxWinners.length">–</span>
                    </template>
                    <template v-slot:[`item.lastShoppingAt`]="{ item }">
                        <span class="date-cell">{{ formatDate(item.lastShoppingAt) }}</span>
                    </template>
                </v-data-table>
            </v-card>

            <!-- Shopping Items -->
            <v-card class="rounded_card quaroshadow pa-4 mt-4">
                <div class="d-flex align-center flex-wrap mb-2">
                    <div>
                        <div class="chart-title d-flex align-center">
                            Shopping Items
                            <v-tooltip top max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                </template>
                                <span>Every product seen in a carousel, aggregated by title across the range — with prices, ratings, position and the internal queries that surfaced it.</span>
                            </v-tooltip>
                        </div>
                        <div class="chart-subtitle mb-0">Detailed list of shopping items and providers</div>
                    </div>
                    <v-spacer />
                    <v-text-field v-model="itemSearch" class="table-search mr-2" rounded dense filled clearable clear-icon="mdi-close" prepend-inner-icon="mdi-magnify" label="Search items or providers..." single-line hide-details />
                    <v-btn icon color="primary" :disabled="!itemsTotal" :loading="exportingItems" @click="exportItems" aria-label="Export items">
                        <v-icon>mdi-cloud-download</v-icon>
                    </v-btn>
                </div>
                <v-data-table
                    :headers="itemHeaders"
                    :items="items"
                    :loading="loadingItems"
                    :server-items-length="itemsTotal"
                    :options.sync="itemOptions"
                    :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
                    class="transparent-table item-table"
                    @click:row="openItemPanel">
                    <!-- Spalten-Tooltips aus der Header-Definition -->
                    <template v-for="h in itemHeaders.filter((x) => x.tooltip)" v-slot:[`header.${h.value}`]="{ header }">
                        <span :key="h.value" class="sv-head-wrap">
                            <span>{{ header.text }}</span>
                            <v-tooltip top max-width="330">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                </template>
                                <span>{{ h.tooltip }}</span>
                            </v-tooltip>
                        </span>
                    </template>
                    <template v-slot:[`item.title`]="{ item }">
                        <span class="d-flex align-center py-1">
                            <img v-if="item.image && !brokenImages[item.title]" :src="item.image" class="item-thumb mr-2" alt="" @error="$set(brokenImages, item.title, true)" />
                            <span v-else class="item-thumb item-thumb--placeholder mr-2"><v-icon small>mdi-package-variant-closed</v-icon></span>
                            <span class="item-title" :title="item.title">{{ item.title }}</span>
                        </span>
                    </template>
                    <template v-slot:[`item.merchants`]="{ item }">
                        <span>{{ (item.merchants || []).slice(0, 2).join(", ") || "–" }}</span>
                        <span v-if="(item.merchants || []).length > 2" class="grey--text"> +{{ item.merchants.length - 2 }}</span>
                    </template>
                    <template v-slot:[`item.visibility`]="{ item }">
                        <div class="d-flex align-center">
                            <v-icon x-small class="mr-1" color="grey">mdi-eye-outline</v-icon>
                            <span>{{ itemVisibilityPct(item) }}%</span>
                        </div>
                    </template>
                    <template v-slot:[`item.priceAvg`]="{ item }">
                        <div v-if="item.priceAvg != null">
                            <strong>{{ formatPrice(item.priceAvg, item.currency) }}</strong>
                            <div v-if="item.priceMin != null && item.priceMax != null && item.priceMin !== item.priceMax" class="price-range">
                                {{ formatPrice(item.priceMin, item.currency) }} – {{ formatPrice(item.priceMax, item.currency) }}
                            </div>
                        </div>
                        <span v-else>–</span>
                    </template>
                    <template v-slot:[`item.ratingValue`]="{ item }">
                        <span v-if="item.ratingValue != null" class="d-flex align-center">
                            <v-icon x-small color="amber" class="mr-1">mdi-star</v-icon>
                            {{ item.ratingValue.toFixed(1) }}
                            <span v-if="item.ratingVotes" class="grey--text ml-1">({{ formatNumber(item.ratingVotes) }})</span>
                        </span>
                        <span v-else>–</span>
                    </template>
                    <template v-slot:[`item.rankAvg`]="{ item }">{{ item.rankAvg ? "#" + item.rankAvg.toFixed(1) : "–" }}</template>
                    <template v-slot:[`item.shoppingQueries`]="{ item }">
                        <v-tooltip top max-width="400" :disabled="!(item.shoppingQueries || []).length">
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip v-bind="attrs" v-on="on" x-small outlined :disabled="!(item.shoppingQueries || []).length">
                                    {{ (item.shoppingQueries || []).length }} queries
                                </v-chip>
                            </template>
                            <div v-for="q in item.shoppingQueries" :key="q">• {{ q }}</div>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`item.lastSeen`]="{ item }">
                        <div class="date-cell">Last: {{ formatDate(item.lastSeen) }}</div>
                        <div class="date-cell grey--text">First: {{ formatDate(item.firstSeen) }}</div>
                    </template>
                </v-data-table>
            </v-card>

            <!-- Query-Detail-Panel (rechts) -->
            <v-dialog v-model="queryPanelOpen" max-width="520" transition="slide-x-reverse-transition" content-class="shopping-item-dialog" overlay-opacity="0.35">
                <v-card v-if="selectedQuery" class="item-panel-card">
                    <v-toolbar flat class="item-panel-toolbar">
                        <v-toolbar-title>
                            <div class="item-panel-title">Shopping Query</div>
                            <div class="item-panel-subtitle">The internal catalog search ChatGPT ran — verbatim</div>
                        </v-toolbar-title>
                        <v-spacer />
                        <v-btn icon @click="queryPanelOpen = false" aria-label="Close">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-toolbar>

                    <v-card-text class="pt-3">
                        <div class="d-flex align-start mb-3">
                            <v-icon class="mr-3" color="grey lighten-1">mdi-magnify</v-icon>
                            <div class="item-panel-name">{{ selectedQuery.query }}</div>
                        </div>

                        <v-row dense class="mb-1">
                            <v-col cols="3">
                                <div class="item-panel-stat">
                                    <div class="item-panel-stat__value">{{ selectedQuery.uses }}</div>
                                    <div class="item-panel-stat__label">Uses</div>
                                </div>
                            </v-col>
                            <v-col cols="3">
                                <div class="item-panel-stat">
                                    <div class="item-panel-stat__value">{{ selectedQuery.runs }}</div>
                                    <div class="item-panel-stat__label">Runs</div>
                                </div>
                            </v-col>
                            <v-col cols="3">
                                <div class="item-panel-stat">
                                    <div class="item-panel-stat__value">{{ selectedQuery.productCount }}</div>
                                    <div class="item-panel-stat__label">Products</div>
                                </div>
                            </v-col>
                            <v-col cols="3">
                                <div class="item-panel-stat">
                                    <div class="item-panel-stat__value">{{ selectedQuery.dayCount }}d</div>
                                    <div class="item-panel-stat__label">Days seen</div>
                                </div>
                            </v-col>
                        </v-row>

                        <!-- Prompts, die diese Query ausgelöst haben -->
                        <div class="item-panel-section">
                            <div class="item-panel-section__title"><v-icon x-small class="mr-1">mdi-message-text-outline</v-icon>Triggered by prompts</div>
                            <template v-if="(selectedQuery.prompts || []).length">
                                <div v-for="p in selectedQuery.prompts" :key="p" class="item-panel-prompt" @click="openPromptFromQuery(p)">
                                    <v-icon x-small class="mr-2" color="grey">mdi-open-in-new</v-icon>
                                    <span class="item-panel-prompt__text">{{ p }}</span>
                                </div>
                            </template>
                            <div v-else class="item-panel-empty">No prompts recorded</div>
                        </div>

                        <!-- Produkte, die diese Query zurückgegeben hat -->
                        <div class="item-panel-section">
                            <div class="item-panel-section__title"><v-icon x-small class="mr-1">mdi-package-variant-closed</v-icon>Products returned</div>
                            <v-progress-linear v-if="queryPanelLoading" indeterminate height="2" class="mb-2" />
                            <template v-else-if="queryPanelItems.length">
                                <div v-for="item in queryPanelItems" :key="item.title" class="item-panel-product" @click="openItemPanel(item)">
                                    <img v-if="item.image" :src="item.image" class="item-panel-product__img mr-2" alt="" />
                                    <span v-else class="item-panel-product__img item-panel-product__img--placeholder mr-2"><v-icon x-small>mdi-package-variant-closed</v-icon></span>
                                    <div class="item-panel-product__body">
                                        <div class="item-panel-product__title">{{ item.title }}</div>
                                        <div class="item-panel-product__meta">
                                            {{ item.appearances }}× · {{ item.rankAvg ? "#" + item.rankAvg.toFixed(1) : "–" }}
                                            <span v-if="item.priceAvg != null"> · {{ formatPrice(item.priceAvg, item.currency) }}</span>
                                        </div>
                                    </div>
                                    <v-icon x-small color="grey">mdi-chevron-right</v-icon>
                                </div>
                            </template>
                            <div v-else class="item-panel-empty">No products found for this query</div>
                        </div>

                        <!-- Provider -->
                        <div class="item-panel-section">
                            <div class="item-panel-section__title"><v-icon x-small class="mr-1">mdi-storefront-outline</v-icon>Providers</div>
                            <template v-if="(selectedQuery.merchants || []).length">
                                <v-chip v-for="m in selectedQuery.merchants" :key="m" x-small outlined class="mr-1 mb-1 merchant-chip" @click="setMerchantFilterFromQuery(m)">{{ m }}</v-chip>
                            </template>
                            <div v-else class="item-panel-empty">No providers recorded</div>
                        </div>

                        <!-- Preis-Range -->
                        <div class="item-panel-section">
                            <div class="item-panel-section__title"><v-icon x-small class="mr-1">mdi-currency-eur</v-icon>Price range</div>
                            <div v-if="selectedQuery.priceAvg != null" class="d-flex align-baseline">
                                <span class="item-panel-price">{{ formatPrice(selectedQuery.priceAvg, selectedQuery.currency) }}</span>
                                <span class="item-panel-price-sub ml-2">avg</span>
                                <span v-if="selectedQuery.priceMin != null && selectedQuery.priceMax != null" class="item-panel-price-sub ml-3">
                                    {{ formatPrice(selectedQuery.priceMin, selectedQuery.currency) }} – {{ formatPrice(selectedQuery.priceMax, selectedQuery.currency) }}
                                </span>
                            </div>
                            <div v-else class="item-panel-empty">No price data</div>
                        </div>

                        <div class="item-panel-seen">
                            First seen {{ formatDate(selectedQuery.firstSeen) }} · Last seen {{ formatDate(selectedQuery.lastSeen) }} · seen on {{ selectedQuery.dayCount }}
                            {{ selectedQuery.dayCount === 1 ? "day" : "days" }}
                        </div>
                    </v-card-text>
                </v-card>
            </v-dialog>

            <!-- Provider-Detail-Panel (rechts) -->
            <v-dialog v-model="providerPanelOpen" max-width="520" transition="slide-x-reverse-transition" content-class="shopping-item-dialog" overlay-opacity="0.35">
                <v-card v-if="selectedProvider" class="item-panel-card">
                    <v-toolbar flat class="item-panel-toolbar">
                        <v-toolbar-title>
                            <div class="item-panel-title">Provider</div>
                            <div class="item-panel-subtitle">Products and prompts of this provider in the selected range</div>
                        </v-toolbar-title>
                        <v-spacer />
                        <v-btn icon @click="providerPanelOpen = false" aria-label="Close">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-toolbar>

                    <v-card-text class="pt-3">
                        <!-- Kopf: Favicon + Name -->
                        <div class="d-flex align-center mb-3">
                            <img v-if="selectedProvider.merchant_domain" :src="faviconUrl(selectedProvider.merchant_domain)" class="item-panel-favicon mr-3" alt="" />
                            <v-icon v-else class="mr-3" color="grey lighten-1">mdi-storefront-outline</v-icon>
                            <div>
                                <div class="item-panel-name">
                                    {{ selectedProvider.merchant_label || selectedProvider.merchant }}
                                    <v-icon v-if="isBrandMerchant(selectedProvider)" small color="primary" class="ml-1">mdi-star</v-icon>
                                </div>
                                <div v-if="selectedProvider.merchant_domain" class="item-panel-subtitle">{{ selectedProvider.merchant_domain }}</div>
                            </div>
                        </div>

                        <!-- Kennzahlen -->
                        <v-row dense class="mb-1">
                            <v-col cols="3">
                                <div class="item-panel-stat">
                                    <div class="item-panel-stat__value">{{ selectedProvider.appearances }}</div>
                                    <div class="item-panel-stat__label">Appearances</div>
                                </div>
                            </v-col>
                            <v-col cols="3">
                                <div class="item-panel-stat">
                                    <div class="item-panel-stat__value">{{ selectedProvider.buyboxWins }}</div>
                                    <div class="item-panel-stat__label">Buybox Wins</div>
                                </div>
                            </v-col>
                            <v-col cols="3">
                                <div class="item-panel-stat">
                                    <div class="item-panel-stat__value">{{ selectedProvider.rankAvg ? "#" + selectedProvider.rankAvg.toFixed(1) : "–" }}</div>
                                    <div class="item-panel-stat__label">Avg Rank</div>
                                </div>
                            </v-col>
                            <v-col cols="3">
                                <div class="item-panel-stat">
                                    <div class="item-panel-stat__value">{{ selectedProvider.share != null ? selectedProvider.share.toFixed(1) + "%" : "–" }}</div>
                                    <div class="item-panel-stat__label">Share</div>
                                </div>
                            </v-col>
                        </v-row>

                        <!-- Produkte des Providers -->
                        <div class="item-panel-section">
                            <div class="item-panel-section__title"><v-icon x-small class="mr-1">mdi-package-variant-closed</v-icon>Products</div>
                            <v-progress-linear v-if="providerPanelLoading" indeterminate height="2" class="mb-2" />
                            <template v-else-if="providerPanelItems.length">
                                <div v-for="item in providerPanelItems" :key="item.title" class="item-panel-product" @click="openItemFromProvider(item)">
                                    <img v-if="item.image" :src="item.image" class="item-panel-product__img mr-2" alt="" />
                                    <span v-else class="item-panel-product__img item-panel-product__img--placeholder mr-2"><v-icon x-small>mdi-package-variant-closed</v-icon></span>
                                    <div class="item-panel-product__body">
                                        <div class="item-panel-product__title">{{ item.title }}</div>
                                        <div class="item-panel-product__meta">
                                            {{ item.appearances }}× · {{ item.rankAvg ? "#" + item.rankAvg.toFixed(1) : "–" }}
                                            <span v-if="item.priceAvg != null"> · {{ formatPrice(item.priceAvg, item.currency) }}</span>
                                        </div>
                                    </div>
                                    <v-icon x-small color="grey">mdi-chevron-right</v-icon>
                                </div>
                            </template>
                            <div v-else class="item-panel-empty">No products found for this provider</div>
                        </div>

                        <!-- Prompts mit diesem Provider -->
                        <div class="item-panel-section">
                            <div class="item-panel-section__title"><v-icon x-small class="mr-1">mdi-message-text-outline</v-icon>Appeared in prompts</div>
                            <template v-if="promptsForSelectedProvider.length">
                                <div v-for="p in promptsForSelectedProvider" :key="p.prompt" class="item-panel-prompt" @click="openPromptFromProvider(p)">
                                    <v-icon x-small class="mr-2" color="grey">mdi-open-in-new</v-icon>
                                    <span class="item-panel-prompt__text">{{ p.prompt }}</span>
                                    <span class="item-panel-prompt__badges">
                                        <v-chip x-small class="ml-1" outlined>{{ p.merchantAppearances }}×</v-chip>
                                        <v-chip v-if="p.merchantBuybox" x-small class="ml-1" color="amber lighten-4" text-color="amber darken-4">buybox</v-chip>
                                    </span>
                                </div>
                            </template>
                            <div v-else class="item-panel-empty">No prompts recorded</div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-dialog>

            <!-- Item-Detail-Panel (rechts, Muster wie SourcesTable-URL-Panel) -->
            <v-dialog v-model="itemPanelOpen" max-width="520" transition="slide-x-reverse-transition" content-class="shopping-item-dialog" overlay-opacity="0.35">
                <v-card v-if="selectedShoppingItem" class="item-panel-card">
                    <v-toolbar flat class="item-panel-toolbar">
                        <v-toolbar-title>
                            <div class="item-panel-title">Shopping Item</div>
                            <div class="item-panel-subtitle">Appearances, prices and shopping queries in the selected range</div>
                        </v-toolbar-title>
                        <v-spacer />
                        <v-btn icon @click="itemPanelOpen = false" aria-label="Close">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-toolbar>

                    <v-card-text class="pt-3">
                        <!-- Kopf: Bild + Titel + Merchants -->
                        <div class="d-flex align-start mb-3">
                            <img v-if="selectedShoppingItem.image" :src="selectedShoppingItem.image" class="item-panel-img mr-3" alt="" />
                            <span v-else class="item-panel-img item-panel-img--placeholder mr-3"><v-icon>mdi-package-variant-closed</v-icon></span>
                            <div>
                                <div class="item-panel-name">{{ selectedShoppingItem.title }}</div>
                                <div class="mt-1">
                                    <v-chip v-for="m in selectedShoppingItem.merchants || []" :key="m" x-small outlined class="mr-1 mb-1">{{ m }}</v-chip>
                                </div>
                            </div>
                        </div>

                        <!-- Kennzahlen -->
                        <v-row dense class="mb-1">
                            <v-col cols="4">
                                <div class="item-panel-stat">
                                    <div class="item-panel-stat__value">{{ selectedShoppingItem.appearances }}</div>
                                    <div class="item-panel-stat__label">Appearances</div>
                                </div>
                            </v-col>
                            <v-col cols="4">
                                <div class="item-panel-stat">
                                    <div class="item-panel-stat__value">{{ itemVisibilityPct(selectedShoppingItem) }}%</div>
                                    <div class="item-panel-stat__label">Visibility</div>
                                </div>
                            </v-col>
                            <v-col cols="4">
                                <div class="item-panel-stat">
                                    <div class="item-panel-stat__value">{{ selectedShoppingItem.rankAvg ? "#" + selectedShoppingItem.rankAvg.toFixed(1) : "–" }}</div>
                                    <div class="item-panel-stat__label">Avg Rank</div>
                                </div>
                            </v-col>
                        </v-row>

                        <!-- Preis-Range -->
                        <div class="item-panel-section">
                            <div class="item-panel-section__title"><v-icon x-small class="mr-1">mdi-currency-eur</v-icon>Price range</div>
                            <div v-if="selectedShoppingItem.priceAvg != null" class="d-flex align-baseline">
                                <span class="item-panel-price">{{ formatPrice(selectedShoppingItem.priceAvg, selectedShoppingItem.currency) }}</span>
                                <span class="item-panel-price-sub ml-2">avg</span>
                                <span v-if="selectedShoppingItem.priceMin != null && selectedShoppingItem.priceMax != null" class="item-panel-price-sub ml-3">
                                    {{ formatPrice(selectedShoppingItem.priceMin, selectedShoppingItem.currency) }} – {{ formatPrice(selectedShoppingItem.priceMax, selectedShoppingItem.currency) }}
                                </span>
                            </div>
                            <div v-else class="item-panel-empty">No price data</div>
                            <div v-if="selectedShoppingItem.ratingValue != null" class="item-panel-price-sub mt-1">
                                <v-icon x-small color="amber">mdi-star</v-icon>
                                {{ selectedShoppingItem.ratingValue.toFixed(1) }}
                                <span v-if="selectedShoppingItem.ratingVotes">({{ formatNumber(selectedShoppingItem.ratingVotes) }} reviews)</span>
                            </div>
                        </div>

                        <!-- Shopping-Queries -->
                        <div class="item-panel-section">
                            <div class="item-panel-section__title">
                                <v-icon x-small class="mr-1">mdi-magnify</v-icon>Shopping queries
                                <v-tooltip top max-width="320">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon v-bind="attrs" v-on="on" x-small class="ml-1" color="grey">mdi-help-circle-outline</v-icon>
                                    </template>
                                    <span>Internal search queries ChatGPT used against the shopping catalog when this product was shown.</span>
                                </v-tooltip>
                            </div>
                            <template v-if="(selectedShoppingItem.shoppingQueries || []).length">
                                <v-chip v-for="q in selectedShoppingItem.shoppingQueries" :key="q" x-small class="mr-1 mb-1" color="teal lighten-5" text-color="teal darken-2">{{ q }}</v-chip>
                            </template>
                            <div v-else class="item-panel-empty">No queries recorded</div>
                        </div>

                        <!-- Prompts -->
                        <div class="item-panel-section">
                            <div class="item-panel-section__title"><v-icon x-small class="mr-1">mdi-message-text-outline</v-icon>Appeared in prompts</div>
                            <template v-if="(selectedShoppingItem.prompts || []).length">
                                <div v-for="p in selectedShoppingItem.prompts" :key="p" class="item-panel-prompt" @click="openPromptFromItem(p)">
                                    <v-icon x-small class="mr-2" color="grey">mdi-open-in-new</v-icon>
                                    <span>{{ p }}</span>
                                </div>
                                <div v-if="(selectedShoppingItem.prompts || []).length >= 10" class="item-panel-empty mt-1">Showing the first 10 prompts</div>
                            </template>
                            <div v-else class="item-panel-empty">No prompts recorded</div>
                        </div>

                        <div class="item-panel-seen">First seen {{ formatDate(selectedShoppingItem.firstSeen) }} · Last seen {{ formatDate(selectedShoppingItem.lastSeen) }}</div>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </template>
    </div>
</template>

<script>
    import BarChart from "../../../components/charts/BarChart"
    import { getPlatformMeta } from "../../../utils/platformMeta"

    const CHART_COLORS = ["#635bff", "#00a089", "#ff8a3d", "#e8506e", "#8b5cf6", "#38bdf8", "#f4b942", "#4ade80", "#f472b6", "#94a3b8"]
    const OTHER_COLOR = "#c3cbd4"

    export default {
        name: "AIProjectShoppingView",
        components: { BarChart },
        props: {
            projectId: { type: [String, Number], default: "" },
            project: { type: Object, default: null },
            selectedDates: { type: Array, default: () => [] },
            selectedPlatform: { type: [Object, String], default: null },
            tags: { type: Array, default: () => [] },
            tagsOperator: { type: String, default: "or" },
            dashboardMode: { type: String, default: "absolute" }
        },
        data() {
            return {
                loadingOverview: false,
                loadingProviders: false,
                loadingItems: false,
                loadingPrompts: false,
                overview: { totalRuns: 0, runsWithShopping: 0, uniqueItems: 0, uniqueMerchants: 0, timeline: [] },
                prevOverview: null,
                showZeroPrompts: false,
                providers: [],
                providerTimeline: [],
                items: [],
                itemsTotal: 0,
                itemOptions: { page: 1, itemsPerPage: 10, sortBy: ["appearances"], sortDesc: [true] },
                prompts: [],
                merchantFilter: null,
                providerSearch: "",
                promptSearch: "",
                itemSearch: "",
                exportingItems: false,
                itemPanelOpen: false,
                selectedShoppingItem: null,
                providerPanelOpen: false,
                selectedProvider: null,
                providerPanelItems: [],
                providerPanelLoading: false,
                // Shopping Queries (die internen Katalog-Suchanfragen)
                loadingQueries: false,
                queries: [],
                queriesTotal: 0,
                queryOptions: { page: 1, itemsPerPage: 10, sortBy: ["uses"], sortDesc: [true] },
                querySearch: "",
                queryKeywords: [],
                queryStats: { totalQueries: 0, recurringQueries: 0, promptsWithQueries: 0, avgQueriesPerPrompt: 0, maxQueriesPerPrompt: 0 },
                activeKeyword: null,
                keywordsExpanded: false,
                recurringOnly: false,
                exportingQueries: false,
                queryPanelOpen: false,
                selectedQuery: null,
                queryPanelItems: [],
                queryPanelLoading: false,
                queryHeaders: [
                    {
                        text: "Query",
                        value: "query",
                        tooltip: "The internal search ChatGPT ran against the shopping catalog to build the carousel — verbatim. This is the wording your product feed has to match."
                    },
                    { text: "Uses", value: "uses", align: "end", tooltip: "How many product slots this query produced in total." },
                    { text: "Prompts", value: "promptCount", align: "end", tooltip: "How many of your prompts triggered this query. Almost always exactly one — queries are prompt-specific." },
                    { text: "Products", value: "productCount", align: "end", tooltip: "Number of distinct products this query returned." },
                    { text: "Providers", value: "merchants", sortable: false, tooltip: "Sellers of the products this query returned. Click a chip to filter the dashboard by that provider." },
                    { text: "Buybox", value: "buyboxCount", align: "end", tooltip: "How often a product from this query landed in position 1 of the carousel." },
                    { text: "Avg Price", value: "priceAvg", align: "end", tooltip: "Average price of the products returned for this query, with the min–max range below." },
                    {
                        text: "Days",
                        value: "dayCount",
                        align: "end",
                        tooltip:
                            "Number of distinct import days this exact query was used (with daily imports this equals the number of runs). ChatGPT rephrases its shopping queries on most runs, so multi-day queries are the stable ones worth optimising for."
                    },
                    { text: "Seen", value: "lastSeen", tooltip: "First and last import date this query was observed in the selected range." }
                ],
                brokenImages: {},
                chartKey: 0,
                reqSeq: 0,
                providerHeaders: [
                    { text: "Provider", value: "merchant", tooltip: "The seller shown next to a product in the carousel. Only the first seller is visible in the response; a star marks your own brand." },
                    { text: "Share", value: "share", align: "end", tooltip: "This provider's appearances divided by all provider appearances in the range — its share of visible shopping slots." },
                    { text: "Appearances", value: "appearances", align: "end", tooltip: "How many product slots across all carousels listed this provider. One response with three of its products counts three times." },
                    {
                        text: "Buybox Wins",
                        value: "buyboxWins",
                        align: "end",
                        tooltip: "How often one of this provider's products sat in position 1 of a carousel — the slot users see first. Our equivalent of a buybox win."
                    },
                    { text: "Avg Rank", value: "rankAvg", align: "end", tooltip: "Average position of this provider's products within the carousels (1 = first). Lower is better." },
                    { text: "Responses", value: "responsesWithMerchant", align: "end", tooltip: "Number of individual LLM responses this provider appeared in — regardless of how many of its products were listed." },
                    { text: "Items", value: "uniqueItemsMax", align: "end", tooltip: "Number of distinct products this provider was listed for." }
                ],
                promptHeaders: [
                    { text: "Prompt", value: "prompt", tooltip: "One of your tracked prompts. Click a row to open the full response in the prompt detail view." },
                    {
                        text: "Shopping",
                        value: "shopping",
                        sortable: false,
                        tooltip: "Runs of this prompt that returned a shopping carousel, out of all its runs in the range. Whether a carousel appears varies from run to run."
                    },
                    { text: "Top Providers", value: "topMerchants", sortable: false, tooltip: "Providers appearing most often in this prompt's carousels. Click a chip to filter the whole dashboard by that provider." },
                    { text: "Buybox Winners", value: "buyboxWinners", sortable: false, tooltip: "Providers that reached position 1 of a carousel for this prompt." },
                    { text: "Last Shopping", value: "lastShoppingAt", tooltip: "Most recent import date on which this prompt returned a shopping carousel." }
                ],
                itemHeaders: [
                    { text: "Title", value: "title", tooltip: "Product title exactly as shown in the carousel. Click a row for details, prices and the queries that surfaced it." },
                    { text: "Provider", value: "merchants", sortable: false, tooltip: "Sellers listed for this product across all carousels." },
                    {
                        text: "Visibility",
                        value: "visibility",
                        sortable: false,
                        align: "end",
                        tooltip: "Share of all shopping responses in this range that contained this product. 100% would mean it appeared in every single carousel."
                    },
                    { text: "Appearances", value: "appearances", align: "end", tooltip: "How many carousel slots this product filled in total." },
                    { text: "Avg Rank", value: "rankAvg", align: "end", tooltip: "Average position within the carousel (1 = first)." },
                    { text: "Avg Price", value: "priceAvg", align: "end", tooltip: "Average price across all sightings, with the min–max range below it when the price varied." },
                    { text: "Rating", value: "ratingValue", align: "end", tooltip: "Star rating and number of reviews as reported in the carousel." },
                    {
                        text: "Queries",
                        value: "shoppingQueries",
                        sortable: false,
                        tooltip: "How many distinct internal shopping queries surfaced this product. Hover to see them — they show the wording that actually matched your feed."
                    },
                    { text: "Seen", value: "lastSeen", tooltip: "First and last import date this product was observed in the selected range." }
                ]
            }
        },
        computed: {
            platformNotSupported() {
                const key = this.selectedPlatform?.key || this.selectedPlatform
                return key && key !== "all" && key !== "chatgpt"
            },
            platformLabel() {
                const key = this.selectedPlatform?.key || this.selectedPlatform
                return getPlatformMeta(key).label || this.selectedPlatform?.name || key
            },
            platformLogo() {
                const key = this.selectedPlatform?.key || this.selectedPlatform
                return getPlatformMeta(key).logo || null
            },
            brandDomain() {
                const raw = this.project?.brand?.brand_domain || ""
                if (!raw) return ""
                return raw
                    .replace(/^https?:\/\//, "")
                    .replace(/^www\./, "")
                    .split("/")[0]
                    .toLowerCase()
            },
            dateRange() {
                const dates = [...(this.selectedDates || [])].filter(Boolean).sort()
                if (!dates.length) return null
                return { from: dates[0], to: dates[dates.length - 1] }
            },
            inputSignature() {
                return JSON.stringify({
                    projectId: this.projectId,
                    dateRange: this.dateRange,
                    tags: this.tags,
                    tagsOperator: this.tagsOperator,
                    platform: this.selectedPlatform?.key || this.selectedPlatform
                })
            },
            shoppingSharePct() {
                if (!this.overview.totalRuns) return "0.0"
                return ((this.overview.runsWithShopping / this.overview.totalRuns) * 100).toFixed(1)
            },
            runDays() {
                return (this.overview.timeline || []).length
            },
            // Range-Zeile in den KPI-Cards, Format wie im Start-Dashboard ("04.06 vs 05.06")
            rangeSubtitle() {
                if (!this.dateRange) return ""
                const s = (d) => {
                    const dt = new Date(d)
                    if (isNaN(dt)) return ""
                    return `${String(dt.getUTCDate()).padStart(2, "0")}.${String(dt.getUTCMonth() + 1).padStart(2, "0")}`
                }
                const cur = this.dateRange.from === this.dateRange.to ? s(this.dateRange.from) : `${s(this.dateRange.from)}–${s(this.dateRange.to)}`
                if (!this.prevRange || !this.prevOverview) return cur
                const prev = `${s(this.prevRange.from)}–${s(this.prevRange.to)}`
                return `${prev} vs ${cur}`
            },
            // Vorperiode gleicher Länge, direkt vor der gewählten Range (für die KPI-Deltas)
            prevRange() {
                if (!this.dateRange) return null
                const from = new Date(this.dateRange.from)
                const to = new Date(this.dateRange.to)
                if (isNaN(from) || isNaN(to)) return null
                const dayMs = 86400000
                const lenDays = Math.max(1, Math.round((to - from) / dayMs) + 1)
                const iso = (d) => d.toISOString().slice(0, 10)
                return { from: iso(new Date(from.getTime() - lenDays * dayMs)), to: iso(new Date(from.getTime() - dayMs)) }
            },
            kpiCards() {
                const avg = this.dashboardMode === "average"
                const cur = this.overviewStats(this.overview, avg)
                const prev = this.prevOverview ? this.overviewStats(this.prevOverview, avg) : null
                const fmtVal = (v) => (avg ? (Math.round(v * 10) / 10).toLocaleString("de-DE") : this.formatNumber(v))
                const build = (key, icon, label, curVal, prevVal, tooltip, total) => {
                    let delta = null
                    let isNew = false
                    if (prev != null && prevVal > 0) delta = ((curVal - prevVal) / prevVal) * 100
                    else if (prev != null && prevVal === 0 && curVal > 0) isNew = true
                    return { key, icon, label, value: fmtVal(curVal), total: total != null ? fmtVal(total) : null, delta, isNew, tooltip }
                }
                const suffix = avg ? " (Ø / run day)" : ""
                const modeNote = avg
                    ? " Average mode: the value is divided by the number of days with runs."
                    : " Absolute mode: totals for the whole range."
                const deltaNote = " The percentage compares against the preceding period of equal length."
                return [
                    build(
                        "items",
                        "mdi-package-variant-closed",
                        "Unique Items" + suffix,
                        cur.items,
                        prev?.items,
                        "Distinct products seen across all shopping carousels in this range; identical product titles count once." + modeNote + deltaNote
                    ),
                    build(
                        "providers",
                        "mdi-storefront-outline",
                        "Providers" + suffix,
                        cur.providers,
                        prev?.providers,
                        "Distinct sellers listed next to those products. Only the first seller per product is visible in the response." + modeNote + deltaNote
                    ),
                    build(
                        "runs",
                        "mdi-cart-outline",
                        `Runs with Shopping (${this.shoppingSharePct}%)` + suffix,
                        cur.runsWithShopping,
                        prev?.runsWithShopping,
                        "Responses that contained a shopping carousel, out of all responses in this range. Whether a carousel is shown varies per run — this share is the base rate of shopping in your prompts." +
                            modeNote +
                            deltaNote,
                        cur.totalRuns
                    )
                ]
            },
            totalAppearances() {
                return this.providers.reduce((sum, p) => sum + (p.appearances || 0), 0)
            },
            buyboxLeader() {
                const sorted = [...this.providers].filter((p) => p.buyboxWins > 0).sort((a, b) => b.buyboxWins - a.buyboxWins)
                return sorted[0] || null
            },
            providersWithShare() {
                const total = this.totalAppearances || 1
                return this.providers.map((p) => ({ ...p, share: (p.appearances / total) * 100 }))
            },
            filteredProviders() {
                let rows = this.providersWithShare
                if (this.merchantFilter) {
                    rows = rows.filter((p) => p.merchant === this.merchantFilter || (p.merchant_label || "") === this.merchantFilter)
                }
                const term = (this.providerSearch || "").trim().toLowerCase()
                if (!term) return rows
                return rows.filter((p) => (p.merchant_label || p.merchant || "").toLowerCase().includes(term))
            },
            promptsWithShoppingCount() {
                return this.prompts.filter((p) => p.runsWithShopping > 0).length
            },
            visibleKeywords() {
                return this.keywordsExpanded ? this.queryKeywords : this.queryKeywords.slice(0, 30)
            },
            queriesSubtitle() {
                const s = this.queryStats
                if (!s.totalQueries) return "Search queries ChatGPT sent to the shopping catalog"
                return `${this.formatNumber(s.totalQueries)} queries from ${s.promptsWithQueries} prompts · Ø ${s.avgQueriesPerPrompt} per prompt · ${s.recurringQueries} recur across days`
            },
            // merchantFilter/recurringOnly verfeinern die Server-Seite clientseitig (wie filteredPrompts)
            filteredQueries() {
                let rows = this.queries
                if (this.recurringOnly) rows = rows.filter((q) => (q.dayCount || 0) > 1)
                if (this.merchantFilter) rows = rows.filter((q) => (q.merchants || []).includes(this.merchantFilter))
                return rows
            },
            // Prompts, in denen der im Panel gewählte Provider auftaucht (aus den by-prompt-Daten)
            promptsForSelectedProvider() {
                if (!this.selectedProvider) return []
                const key = this.selectedProvider.merchant
                return this.prompts
                    .map((p) => {
                        const entry = (p.topMerchants || []).find((m) => m.merchant === key)
                        if (!entry) return null
                        return { ...p, merchantAppearances: entry.appearances, merchantBuybox: entry.buyboxWins }
                    })
                    .filter(Boolean)
                    .sort((a, b) => b.merchantAppearances - a.merchantAppearances)
            },
            filteredPrompts() {
                let rows = this.prompts
                if (!this.showZeroPrompts) {
                    rows = rows.filter((p) => p.runsWithShopping > 0)
                }
                if (this.merchantFilter) {
                    rows = rows.filter((p) => p.topMerchants.some((m) => m.merchant === this.merchantFilter))
                }
                const term = (this.promptSearch || "").trim().toLowerCase()
                if (!term) return rows
                return rows.filter((p) => (p.prompt || "").toLowerCase().includes(term) || p.topMerchants.some((m) => m.merchant.toLowerCase().includes(term)))
            },
            // Balkenfarbe: eigene Brand grün, aktiver Filter voll, Rest gedimmt
            providersChartData() {
                const top = this.providers.slice(0, 15)
                return {
                    labels: top.map((p) => p.merchant_label || p.merchant),
                    datasets: [
                        {
                            label: "Appearances",
                            data: top.map((p) => p.appearances),
                            backgroundColor: top.map((p, i) => this.barColor(p, i))
                        }
                    ]
                }
            },
            buyboxChartData() {
                const top = [...this.providers]
                    .filter((p) => p.buyboxWins > 0)
                    .sort((a, b) => b.buyboxWins - a.buyboxWins)
                    .slice(0, 12)
                return {
                    labels: top.map((p) => p.merchant_label || p.merchant),
                    datasets: [
                        {
                            label: "Buybox Wins",
                            data: top.map((p) => p.buyboxWins),
                            backgroundColor: top.map((p, i) => this.barColor(p, i))
                        }
                    ]
                }
            },
            runsChartData() {
                const timeline = this.overview.timeline || []
                return {
                    labels: timeline.map((t) => this.formatDate(t.importDate)),
                    datasets: [
                        { label: "With shopping", data: timeline.map((t) => t.withShopping), backgroundColor: "#00a089" },
                        { label: "Without shopping", data: timeline.map((t) => t.total - t.withShopping), backgroundColor: OTHER_COLOR }
                    ]
                }
            },
            // 100%-gestapelte Buybox-Timeline: Top-8-Merchants + Other
            buyboxTimelineData() {
                const rows = (this.providerTimeline || []).filter((r) => r.buyboxWins > 0)
                if (!rows.length) return { labels: [], datasets: [] }

                const winsByMerchant = new Map()
                rows.forEach((r) => winsByMerchant.set(r.merchant, (winsByMerchant.get(r.merchant) || 0) + r.buyboxWins))
                const topMerchants = [...winsByMerchant.entries()]
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 8)
                    .map(([m]) => m)
                const topSet = new Set(topMerchants)

                const dates = [...new Set(rows.map((r) => r.importDate))].sort()
                const totals = new Map(dates.map((d) => [d, 0]))
                const cell = new Map() // `${date}|${merchant}` -> wins
                rows.forEach((r) => {
                    totals.set(r.importDate, totals.get(r.importDate) + r.buyboxWins)
                    const key = topSet.has(r.merchant) ? r.merchant : "Other"
                    const mapKey = `${r.importDate}|${key}`
                    cell.set(mapKey, (cell.get(mapKey) || 0) + r.buyboxWins)
                })

                const labelFor = (m) => {
                    const p = this.providers.find((x) => x.merchant === m)
                    return p ? p.merchant_label || p.merchant : m
                }
                const seriesKeys = [...topMerchants, "Other"]
                const datasets = seriesKeys.map((m, i) => ({
                    label: m === "Other" ? "Other" : labelFor(m),
                    data: dates.map((d) => {
                        const wins = cell.get(`${d}|${m}`) || 0
                        const total = totals.get(d) || 1
                        return Math.round((wins / total) * 1000) / 10
                    }),
                    backgroundColor: m === "Other" ? OTHER_COLOR : CHART_COLORS[i % CHART_COLORS.length]
                }))
                return { labels: dates.map((d) => this.formatDate(d)), datasets }
            },
            providersChartOptions() {
                return this.buildBarOptions((label) => this.toggleMerchantFilterByLabel(label))
            },
            buyboxChartOptions() {
                return this.buildBarOptions((label) => this.toggleMerchantFilterByLabel(label))
            },
            stackedChartOptions() {
                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: { display: true, position: "bottom", labels: { boxWidth: 12, fontSize: 11 } },
                    scales: {
                        xAxes: [{ stacked: true, ticks: { autoSkip: true, maxTicksLimit: 16, maxRotation: 45, fontSize: 10 }, gridLines: { display: false } }],
                        yAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 } }]
                    },
                    tooltips: { mode: "index", intersect: false }
                }
            },
            buyboxTimelineOptions() {
                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: { display: true, position: "bottom", labels: { boxWidth: 12, fontSize: 11 } },
                    scales: {
                        xAxes: [{ stacked: true, ticks: { autoSkip: true, maxTicksLimit: 16, maxRotation: 45, fontSize: 10 }, gridLines: { display: false } }],
                        yAxes: [{ stacked: true, ticks: { beginAtZero: true, max: 100, callback: (v) => v + "%" } }]
                    },
                    tooltips: {
                        mode: "index",
                        intersect: false,
                        callbacks: {
                            label: (tooltipItem, data) => {
                                const ds = data.datasets[tooltipItem.datasetIndex]
                                const val = ds.data[tooltipItem.index]
                                return val > 0 ? `${ds.label}: ${val}%` : null
                            }
                        }
                    }
                }
            }
        },
        watch: {
            inputSignature() {
                this.fetchAll()
            },
            itemOptions: {
                handler() {
                    this.fetchItems()
                },
                deep: true
            },
            itemSearch() {
                clearTimeout(this._searchDebounce)
                this._searchDebounce = setTimeout(() => {
                    this.itemOptions.page = 1
                    this.fetchItems()
                }, 400)
            },
            queryOptions: {
                handler() {
                    this.fetchQueries()
                },
                deep: true
            },
            querySearch() {
                clearTimeout(this._querySearchDebounce)
                this._querySearchDebounce = setTimeout(() => {
                    this.activeKeyword = null // Tippen hebt einen aktiven Keyword-Chip auf
                    this.queryOptions.page = 1
                    this.fetchQueries()
                }, 400)
            }
        },
        mounted() {
            this.fetchAll()
        },
        methods: {
            resolveApiRoot() {
                return JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                    this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                ) == -1
                    ? process.env.VUE_APP_MONITORING_API_URL
                    : process.env.VUE_APP_APIURL + "/monitoring-api"
            },
            payloadBase() {
                const payload = {
                    company_id: localStorage.company_id,
                    project_id: this.projectId || this.$route.params.id,
                    platform: "chatgpt"
                }
                if (this.dateRange) {
                    payload.importDateRange = { from: this.dateRange.from, to: this.dateRange.to }
                }
                if (this.tags && this.tags.length) {
                    payload.tags = this.tags
                    payload.tagsOperator = this.tagsOperator
                }
                return payload
            },
            async fetchAll() {
                if (this.platformNotSupported || !this.dateRange) return
                const seq = ++this.reqSeq
                this.fetchOverview(seq)
                this.fetchProviders(seq)
                this.fetchByPrompt(seq)
                this.fetchItems(seq)
                this.fetchQueries(seq)
            },
            async fetchQueries(seq = this.reqSeq) {
                if (this.platformNotSupported || !this.dateRange) return
                this.loadingQueries = true
                try {
                    const sortField = this.queryOptions.sortBy?.[0] || "uses"
                    const sortDir = this.queryOptions.sortDesc?.[0] === false ? 1 : -1
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/shopping/queries", {
                        ...this.payloadBase(),
                        skip: (this.queryOptions.page - 1) * this.queryOptions.itemsPerPage,
                        limit: this.queryOptions.itemsPerPage,
                        querySearch: this.activeKeyword || this.querySearch || undefined,
                        sort: { [sortField]: sortDir }
                    })
                    if (seq !== this.reqSeq) return
                    this.queries = Array.isArray(resp.data?.queries) ? resp.data.queries : []
                    this.queriesTotal = resp.data?.total || 0
                    this.queryKeywords = Array.isArray(resp.data?.keywords) ? resp.data.keywords : []
                    if (resp.data?.stats) this.queryStats = resp.data.stats
                } catch (e) {
                    console.error("shopping/queries failed", e)
                } finally {
                    if (seq === this.reqSeq) this.loadingQueries = false
                }
            },
            // ---- Keyword-Chips ----
            keywordTier(kw) {
                const total = this.queryStats.totalQueries || 1
                const share = (kw.queryCount / total) * 100
                if (share >= 10) return "kw-chip--lg"
                if (share >= 4) return "kw-chip--md"
                return "kw-chip--sm"
            },
            toggleKeyword(term) {
                this.activeKeyword = this.activeKeyword === term ? null : term
                this.querySearch = ""
                this.queryOptions.page = 1
                this.fetchQueries()
            },
            // ---- Query-Detail-Panel ----
            async openQueryPanel(row) {
                this.selectedQuery = row
                this.queryPanelOpen = true
                this.queryPanelItems = []
                this.queryPanelLoading = true
                try {
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/shopping/items", {
                        ...this.payloadBase(),
                        skip: 0,
                        limit: 20,
                        shoppingQuery: row.query,
                        sort: { appearances: -1 }
                    })
                    if (this.selectedQuery?.query !== row.query) return
                    this.queryPanelItems = Array.isArray(resp.data?.items) ? resp.data.items : []
                } catch (e) {
                    console.error("query panel items failed", e)
                } finally {
                    this.queryPanelLoading = false
                }
            },
            openPromptFromQuery(prompt) {
                if (!prompt) return
                this.queryPanelOpen = false
                this.$emit("openPrompt", { prompt, lastShoppingAt: this.selectedQuery?.lastSeen })
            },
            setMerchantFilterFromQuery(merchant) {
                this.queryPanelOpen = false
                this.setMerchantFilter(merchant)
            },
            async fetchOverview(seq = this.reqSeq) {
                this.loadingOverview = true
                try {
                    const normalize = (data) => ({
                        totalRuns: data?.totalRuns || 0,
                        runsWithShopping: data?.runsWithShopping || 0,
                        uniqueItems: data?.uniqueItems || 0,
                        uniqueMerchants: data?.uniqueMerchants || 0,
                        timeline: Array.isArray(data?.timeline) ? data.timeline : []
                    })
                    const url = this.resolveApiRoot() + "/ai-suite/shopping/overview"
                    const currentPromise = this.$helpers.axiosPost(url, this.payloadBase())
                    // Vorperiode gleicher Länge für die KPI-Deltas (Fehler dort blockieren nichts)
                    const prevPromise = this.prevRange ? this.$helpers.axiosPost(url, { ...this.payloadBase(), importDateRange: this.prevRange }).catch(() => null) : Promise.resolve(null)
                    const [resp, prevResp] = await Promise.all([currentPromise, prevPromise])
                    if (seq !== this.reqSeq) return
                    this.overview = normalize(resp.data)
                    this.prevOverview = prevResp && prevResp.data && prevResp.data.totalRuns > 0 ? normalize(prevResp.data) : null
                    this.chartKey++
                } catch (e) {
                    console.error("shopping/overview failed", e)
                } finally {
                    if (seq === this.reqSeq) this.loadingOverview = false
                }
            },
            // Kennzahlen je Modus: absolute Summen oder Ø pro Run-Tag (aus der Timeline)
            overviewStats(overview, avg) {
                if (!avg) {
                    return {
                        items: overview.uniqueItems,
                        providers: overview.uniqueMerchants,
                        runsWithShopping: overview.runsWithShopping,
                        totalRuns: overview.totalRuns
                    }
                }
                const timeline = overview.timeline || []
                const days = timeline.length || 1
                const sum = (key) => timeline.reduce((acc, t) => acc + (Number(t[key]) || 0), 0)
                return {
                    items: sum("uniqueItems") / days,
                    providers: sum("uniqueMerchants") / days,
                    runsWithShopping: overview.runsWithShopping / days,
                    totalRuns: overview.totalRuns / days
                }
            },
            async fetchProviders(seq = this.reqSeq) {
                this.loadingProviders = true
                try {
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/shopping/providers", this.payloadBase())
                    if (seq !== this.reqSeq) return
                    this.providers = Array.isArray(resp.data?.providers) ? resp.data.providers : []
                    this.providerTimeline = Array.isArray(resp.data?.timeline) ? resp.data.timeline : []
                    this.chartKey++
                } catch (e) {
                    console.error("shopping/providers failed", e)
                } finally {
                    if (seq === this.reqSeq) this.loadingProviders = false
                }
            },
            async fetchItems(seq = this.reqSeq) {
                if (this.platformNotSupported || !this.dateRange) return
                this.loadingItems = true
                try {
                    const sortField = this.itemOptions.sortBy?.[0] || "appearances"
                    const sortDir = this.itemOptions.sortDesc?.[0] === false ? 1 : -1
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/shopping/items", {
                        ...this.payloadBase(),
                        skip: (this.itemOptions.page - 1) * this.itemOptions.itemsPerPage,
                        limit: this.itemOptions.itemsPerPage,
                        itemSearch: this.merchantFilter || this.itemSearch || undefined,
                        sort: { [sortField]: sortDir }
                    })
                    if (seq !== this.reqSeq) return
                    this.items = Array.isArray(resp.data?.items) ? resp.data.items : []
                    this.itemsTotal = resp.data?.total || 0
                } catch (e) {
                    console.error("shopping/items failed", e)
                } finally {
                    if (seq === this.reqSeq) this.loadingItems = false
                }
            },
            async fetchByPrompt(seq = this.reqSeq) {
                this.loadingPrompts = true
                try {
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/shopping/by-prompt", {
                        ...this.payloadBase(),
                        limit: 200
                    })
                    if (seq !== this.reqSeq) return
                    this.prompts = Array.isArray(resp.data?.prompts) ? resp.data.prompts : []
                } catch (e) {
                    console.error("shopping/by-prompt failed", e)
                } finally {
                    if (seq === this.reqSeq) this.loadingPrompts = false
                }
            },
            // ---- Merchant-Filter (Klick auf Chart-Balken oder Chip) ----
            buildBarOptions(onBarClick) {
                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: { display: false },
                    onClick: (evt, elements) => {
                        if (elements && elements.length) {
                            const idx = elements[0]._index
                            const label = elements[0]._chart.data.labels[idx]
                            onBarClick(label)
                        }
                    },
                    scales: {
                        xAxes: [{ ticks: { autoSkip: false, maxRotation: 60, minRotation: 45, fontSize: 10 }, gridLines: { display: false } }],
                        yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }]
                    }
                }
            },
            toggleMerchantFilterByLabel(label) {
                const provider = this.providers.find((p) => (p.merchant_label || p.merchant) === label)
                const key = provider ? provider.merchant : label
                if (this.merchantFilter === key) {
                    this.clearMerchantFilter()
                } else {
                    this.setMerchantFilter(key)
                }
            },
            setMerchantFilter(merchant) {
                this.merchantFilter = merchant
                this.itemOptions.page = 1
                this.fetchItems()
            },
            clearMerchantFilter() {
                this.merchantFilter = null
                this.itemOptions.page = 1
                this.fetchItems()
            },
            barColor(provider, index) {
                const base = this.isBrandMerchant(provider) ? "#00a089" : CHART_COLORS[index % CHART_COLORS.length]
                if (!this.merchantFilter) return base
                return provider.merchant === this.merchantFilter ? base : base + "33"
            },
            isBrandMerchant(provider) {
                if (!this.brandDomain) return false
                const dom = (provider.merchant_domain || provider.merchant || "").toLowerCase()
                return dom === this.brandDomain || dom.endsWith("." + this.brandDomain)
            },
            providerRowClass(item) {
                return this.isBrandMerchant(item) ? "provider-row--brand" : ""
            },
            itemVisibilityPct(item) {
                if (!this.overview.runsWithShopping) return "0.0"
                const responses = item.responsesCount || 0
                return ((responses / this.overview.runsWithShopping) * 100).toFixed(1)
            },
            // ---- Item-Detail-Panel ----
            openItemPanel(item) {
                this.selectedShoppingItem = item
                this.itemPanelOpen = true
            },
            // ---- Provider-Detail-Panel ----
            async openProviderPanel(provider) {
                this.selectedProvider = provider
                this.providerPanelOpen = true
                this.providerPanelItems = []
                this.providerPanelLoading = true
                try {
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/shopping/items", {
                        ...this.payloadBase(),
                        skip: 0,
                        limit: 15,
                        itemSearch: provider.merchant,
                        sort: { appearances: -1 }
                    })
                    if (this.selectedProvider?.merchant !== provider.merchant) return
                    // Nur Items behalten, bei denen der Provider wirklich als Merchant gelistet ist
                    // (die Server-Suche matcht auch Produkt-Titel); Fallback: ungefilterte Treffer.
                    const rows = Array.isArray(resp.data?.items) ? resp.data.items : []
                    const merchantKey = (provider.merchant || "").toLowerCase()
                    const filtered = rows.filter((i) => (i.merchants || []).some((m) => (m || "").toLowerCase() === merchantKey))
                    this.providerPanelItems = filtered.length ? filtered : rows
                } catch (e) {
                    console.error("provider panel items failed", e)
                } finally {
                    this.providerPanelLoading = false
                }
            },
            // Produkt im Provider-Panel angeklickt -> Item-Panel legt sich darüber
            // (Provider-Panel bleibt offen; Schließen des Item-Panels führt dorthin zurück)
            openItemFromProvider(item) {
                this.openItemPanel(item)
            },
            openPromptFromProvider(promptRow) {
                if (!promptRow?.prompt) return
                this.providerPanelOpen = false
                this.$emit("openPrompt", promptRow)
            },
            // Prompt im Panel angeklickt -> PromptOverview mit dem letzten Shopping-Datum des Items
            openPromptFromItem(prompt) {
                if (!prompt) return
                this.itemPanelOpen = false
                this.$emit("openPrompt", { prompt, lastShoppingAt: this.selectedShoppingItem?.lastSeen })
            },
            // ---- CSV-Export ----
            downloadCsv(filename, headers, rows) {
                const esc = (v) => {
                    const s = v == null ? "" : String(v)
                    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
                }
                const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n")
                const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" })
                const link = document.createElement("a")
                link.href = URL.createObjectURL(blob)
                link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(link.href)
            },
            exportProviders() {
                this.downloadCsv(
                    "shopping_providers",
                    ["Provider", "Domain", "Share %", "Appearances", "Buybox Wins", "Avg Rank", "Responses", "Items"],
                    this.filteredProviders.map((p) => [p.merchant_label || p.merchant, p.merchant_domain || "", p.share.toFixed(1), p.appearances, p.buyboxWins, p.rankAvg ? p.rankAvg.toFixed(2) : "", p.responsesWithMerchant, p.uniqueItemsMax])
                )
            },
            exportKeywords() {
                this.downloadCsv(
                    "shopping_query_keywords",
                    ["Term", "Queries Containing Term", "Share %"],
                    this.queryKeywords.map((k) => [k.term, k.queryCount, this.queryStats.totalQueries ? ((k.queryCount / this.queryStats.totalQueries) * 100).toFixed(1) : ""])
                )
            },
            async exportQueries() {
                this.exportingQueries = true
                try {
                    const sortField = this.queryOptions.sortBy?.[0] || "uses"
                    const sortDir = this.queryOptions.sortDesc?.[0] === false ? 1 : -1
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/shopping/queries", {
                        ...this.payloadBase(),
                        skip: 0,
                        limit: 1000,
                        querySearch: this.activeKeyword || this.querySearch || undefined,
                        sort: { [sortField]: sortDir }
                    })
                    const rows = Array.isArray(resp.data?.queries) ? resp.data.queries : []
                    this.downloadCsv(
                        "shopping_queries",
                        ["Query", "Uses", "Runs", "Prompts", "Top Prompts", "Products", "Providers", "Top Providers", "Buybox", "Avg Rank", "Avg Price", "Min Price", "Max Price", "Currency", "Days Seen", "First Seen", "Last Seen"],
                        rows.map((q) => [
                            q.query,
                            q.uses,
                            q.runs,
                            q.promptCount,
                            (q.prompts || []).join(" | "),
                            q.productCount,
                            q.merchantCount,
                            (q.merchants || []).join(" | "),
                            q.buyboxCount,
                            q.rankAvg != null ? q.rankAvg.toFixed(2) : "",
                            q.priceAvg != null ? q.priceAvg.toFixed(2) : "",
                            q.priceMin != null ? q.priceMin.toFixed(2) : "",
                            q.priceMax != null ? q.priceMax.toFixed(2) : "",
                            q.currency || "",
                            q.dayCount,
                            this.formatDate(q.firstSeen),
                            this.formatDate(q.lastSeen)
                        ])
                    )
                } catch (e) {
                    console.error("shopping queries export failed", e)
                } finally {
                    this.exportingQueries = false
                }
            },
            exportPrompts() {
                this.downloadCsv(
                    "shopping_prompts",
                    ["Prompt", "Runs With Shopping", "Total Runs", "Top Providers", "Buybox Winners", "Last Shopping"],
                    this.filteredPrompts.map((p) => [
                        p.prompt,
                        p.runsWithShopping,
                        p.totalRuns,
                        p.topMerchants.map((m) => m.merchant).join(" | "),
                        p.buyboxWinners.map((m) => m.merchant).join(" | "),
                        p.lastShoppingAt ? this.formatDate(p.lastShoppingAt) : ""
                    ])
                )
            },
            async exportItems() {
                this.exportingItems = true
                try {
                    const sortField = this.itemOptions.sortBy?.[0] || "appearances"
                    const sortDir = this.itemOptions.sortDesc?.[0] === false ? 1 : -1
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/shopping/items", {
                        ...this.payloadBase(),
                        skip: 0,
                        limit: 1000,
                        itemSearch: this.merchantFilter || this.itemSearch || undefined,
                        sort: { [sortField]: sortDir }
                    })
                    const rows = Array.isArray(resp.data?.items) ? resp.data.items : []
                    this.downloadCsv(
                        "shopping_items",
                        ["Title", "Providers", "Visibility %", "Appearances", "Avg Rank", "Avg Price", "Min Price", "Max Price", "Currency", "Rating", "Rating Votes", "Shopping Queries", "First Seen", "Last Seen"],
                        rows.map((i) => [
                            i.title,
                            (i.merchants || []).join(" | "),
                            this.itemVisibilityPct(i),
                            i.appearances,
                            i.rankAvg ? i.rankAvg.toFixed(2) : "",
                            i.priceAvg != null ? i.priceAvg.toFixed(2) : "",
                            i.priceMin != null ? i.priceMin.toFixed(2) : "",
                            i.priceMax != null ? i.priceMax.toFixed(2) : "",
                            i.currency || "",
                            i.ratingValue != null ? i.ratingValue.toFixed(1) : "",
                            i.ratingVotes || "",
                            (i.shoppingQueries || []).join(" | "),
                            this.formatDate(i.firstSeen),
                            this.formatDate(i.lastSeen)
                        ])
                    )
                } catch (e) {
                    console.error("shopping items export failed", e)
                } finally {
                    this.exportingItems = false
                }
            },
            faviconUrl(domain) {
                return "https://s2.googleusercontent.com/s2/favicons?domain=" + domain + "&sz=32"
            },
            formatNumber(value) {
                return Number(value || 0).toLocaleString("de-DE")
            },
            formatPrice(value, currency) {
                if (value == null) return "–"
                const cur = currency === "EUR" ? "€" : currency || ""
                return `${cur}${Number(value).toFixed(2)}`
            },
            formatDate(value) {
                if (!value) return "–"
                const d = new Date(value)
                if (isNaN(d)) return "–"
                return `${String(d.getUTCDate()).padStart(2, "0")}.${String(d.getUTCMonth() + 1).padStart(2, "0")}.${String(d.getUTCFullYear()).slice(2)}`
            }
        }
    }
</script>

<style scoped>
    .kpi-card {
        border-top: 3px solid var(--v-primary-base);
    }
    .kpi-value {
        font-size: 1.6rem;
        font-weight: 600;
        line-height: 1.2;
    }
    .kpi-value--small {
        font-size: 1.1rem;
    }
    .kpi-leader-name {
        max-width: 190px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .kpi-total {
        font-size: 0.95rem;
        font-weight: 400;
        color: #8a94a6;
        margin-left: 4px;
    }
    .kpi-label {
        font-size: 0.8rem;
        color: #8a94a6;
    }
    .chart-title {
        font-size: 1rem;
        font-weight: 600;
    }
    .chart-subtitle {
        font-size: 0.75rem;
        color: #8a94a6;
        margin-bottom: 12px;
    }
    .chart-wrap {
        position: relative;
        height: 320px;
    }
    .chart-wrap > div {
        height: 100%;
    }
    .chart-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: #8a94a6;
        font-size: 0.85rem;
    }
    .filter-hint {
        font-size: 0.75rem;
        color: #8a94a6;
    }
    /* eigene Notice statt v-alert: der "text"-Variant rendert hier transparent mit weißem Rand */
    .shopping-notice {
        display: flex;
        align-items: center;
        padding: 10px 14px;
        border: 1px solid rgba(0, 160, 137, 0.3);
        border-left: 3px solid var(--v-primary-base);
        border-radius: 8px;
        background: rgba(0, 160, 137, 0.06);
        font-size: 0.82rem;
        color: #3c4654;
    }
    .shopping-notice__hint {
        color: #8a94a6;
        margin-left: 4px;
    }
    .theme--dark .shopping-notice {
        background: rgba(0, 160, 137, 0.12);
        color: #dfe3e8;
    }
    .kpi-range {
        font-size: 0.68rem;
        color: #b0b8c4;
        margin-top: 2px;
    }
    .kpi-range--indent {
        margin-left: 36px;
    }
    .prompt-table >>> tbody tr,
    .item-table >>> tbody tr,
    .provider-table >>> tbody tr,
    .query-table >>> tbody tr {
        cursor: pointer;
    }
    /* Keyword-Chips: Größe signalisiert Häufigkeit */
    .sv-head-wrap {
        display: inline-flex;
        align-items: center;
    }
    /* Kopfzeilen wie in der Prompts-Tabelle: leicht und luftig statt fett */
    .transparent-table >>> thead th {
        white-space: nowrap !important;
        text-transform: uppercase !important;
        font-size: 12px !important;
        letter-spacing: 0.1px !important;
        font-weight: 300 !important;
    }
    .transparent-table >>> tbody td {
        padding: 12px 16px !important;
        font-size: 14px;
    }
    .kw-chip {
        margin: 0 4px 6px 0;
        cursor: pointer;
    }
    .kw-chip--lg {
        font-size: 0.85rem;
        font-weight: 600;
    }
    .kw-chip--md {
        font-size: 0.78rem;
    }
    .kw-chip--sm {
        font-size: 0.72rem;
    }
    .kw-chip__count {
        opacity: 0.6;
        margin-left: 5px;
        font-variant-numeric: tabular-nums;
    }
    .keyword-empty {
        font-size: 0.8rem;
        color: #b0b8c4;
        padding: 6px 0;
    }
    .query-cell {
        display: inline-block;
        max-width: 340px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: middle;
    }
    .item-panel-favicon {
        width: 28px;
        height: 28px;
        border-radius: 6px;
    }
    .item-panel-product {
        display: flex;
        align-items: center;
        padding: 6px 8px;
        border-radius: 6px;
        cursor: pointer;
    }
    .item-panel-product:hover {
        background: #f2f7f6;
    }
    .item-panel-product__img {
        width: 30px;
        height: 30px;
        object-fit: contain;
        border-radius: 4px;
        background: #f5f6f8;
        flex: 0 0 30px;
    }
    .item-panel-product__img--placeholder {
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
    .item-panel-product__body {
        flex: 1 1 auto;
        min-width: 0;
    }
    .item-panel-product__title {
        font-size: 0.78rem;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .item-panel-product__meta {
        font-size: 0.7rem;
        color: #8a94a6;
    }
    .item-panel-prompt__text {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .item-panel-prompt__badges {
        flex: 0 0 auto;
        white-space: nowrap;
    }
    /* Item-Detail-Panel */
    .item-panel-toolbar {
        border-bottom: 1px solid #eef1f5;
    }
    .item-panel-title {
        font-size: 1rem;
        font-weight: 600;
        line-height: 1.3;
    }
    .item-panel-subtitle {
        font-size: 0.72rem;
        color: #8a94a6;
    }
    .item-panel-img {
        width: 64px;
        height: 64px;
        object-fit: contain;
        border-radius: 8px;
        background: #f5f6f8;
        flex: 0 0 64px;
    }
    .item-panel-img--placeholder {
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
    .item-panel-name {
        font-size: 0.9rem;
        font-weight: 600;
        line-height: 1.3;
    }
    .item-panel-stat {
        text-align: center;
        background: #f7f8fa;
        border-radius: 8px;
        padding: 8px 4px;
    }
    .item-panel-stat__value {
        font-size: 1rem;
        font-weight: 700;
    }
    .item-panel-stat__label {
        font-size: 0.68rem;
        color: #8a94a6;
    }
    .item-panel-section {
        margin-top: 14px;
    }
    .item-panel-section__title {
        display: flex;
        align-items: center;
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #5b6472;
        margin-bottom: 6px;
    }
    .item-panel-price {
        font-size: 1.1rem;
        font-weight: 700;
    }
    .item-panel-price-sub {
        font-size: 0.75rem;
        color: #8a94a6;
    }
    .item-panel-empty {
        font-size: 0.75rem;
        color: #b0b8c4;
    }
    .item-panel-prompt {
        display: flex;
        align-items: baseline;
        font-size: 0.8rem;
        padding: 6px 8px;
        border-radius: 6px;
        cursor: pointer;
    }
    .item-panel-prompt:hover {
        background: #f2f7f6;
    }
    .item-panel-seen {
        margin-top: 16px;
        font-size: 0.72rem;
        color: #8a94a6;
    }
    .kpi-delta {
        flex: 0 0 auto;
    }
    .zero-prompts-switch >>> .v-label {
        font-size: 0.78rem;
        color: #8a94a6;
    }
    .platform-empty-state {
        max-width: 560px;
        margin: 24px auto 0;
    }
    .platform-empty-state__logo >>> svg {
        width: 44px;
        height: 44px;
        border-radius: 10px;
    }
    .platform-empty-state__title {
        font-size: 1.05rem;
        font-weight: 600;
        margin-bottom: 8px;
    }
    .platform-empty-state__text {
        font-size: 0.85rem;
        color: #8a94a6;
        max-width: 420px;
        margin: 0 auto;
    }
    .table-search {
        max-width: 260px;
        flex: 0 1 260px;
    }
    .merchant-favicon {
        width: 16px;
        height: 16px;
        border-radius: 3px;
    }
    .merchant-chip {
        cursor: pointer;
    }
    .share-value {
        min-width: 44px;
        text-align: right;
        font-variant-numeric: tabular-nums;
    }
    .item-thumb {
        width: 34px;
        height: 34px;
        object-fit: contain;
        border-radius: 4px;
        background: #f5f6f8;
    }
    .item-thumb--placeholder {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 34px;
    }
    .item-title {
        max-width: 320px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .prompt-cell {
        display: inline-block;
        max-width: 420px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: middle;
    }
    .price-range {
        font-size: 0.7rem;
        color: #8a94a6;
    }
    .date-cell {
        font-size: 0.75rem;
        white-space: nowrap;
    }
</style>

<style>
    .shopping-view .provider-row--brand {
        background: rgba(0, 160, 137, 0.07);
    }
    /* content-class des v-dialog liegt außerhalb des Komponenten-Scopes -> unscoped.
       height/max-height brauchen !important, sonst gewinnt Vuetifys .v-dialog { max-height: 90% }
       und der untere Teil des Panels ist nicht erreichbar. */
    .shopping-item-dialog {
        margin: 0 !important;
        margin-left: auto !important;
        height: 100vh !important;
        max-height: 100vh !important;
        display: flex;
        align-items: stretch;
        overflow-y: hidden;
    }
    .shopping-item-dialog .v-card {
        width: 520px;
        height: 100vh;
        max-height: 100vh;
        overflow-y: auto;
        border-radius: 0;
        padding-bottom: 24px;
    }
</style>
